import React, { useEffect, useState } from 'react';
import LoginContainer from './components/login-container';
import EmailLogin from './components/email-login';
import Form from 'antd/es/form';
import { useDispatch } from 'react-redux';
import { setData } from '../../store/Home/authSlice';
import { useNavigate, useLocation, redirect } from 'react-router-dom';
import { message } from 'antd';
import { checkUserStatus, handleUserLogin, handleVerifyCode } from '../../services/api';
import { preserveRedirectInPath, buildFinalRedirect } from '../../utils/redirect';
import { get } from 'lodash';
import { PARAMS } from '../../params';

const Login = () => {
	const [form] = Form.useForm();
	const [isNewUser, setIsNewUser] = useState(false);
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(1);
	const [isForgotPassword, setIsForgotPassword] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	
	// Clear local auth state on component mount.
	//
	// NOTE (removed pre-Option-A cookie shuffle): this effect used to enumerate
	// document.cookie on .dev hosts, delete every cookie on .veytan.com, and
	// re-write auth/token-named ones onto .dev.veytan.com. That predates two
	// things that make it wrong today:
	//   1. Env isolation is now done by NAME (env-suffixed cookies
	//      veytan_auth_*_dev/_stag from server-01), not by domain shuffling.
	//   2. Real session cookies are HttpOnly — invisible to document.cookie —
	//      so the loop only ever touched legacy JS cookies. Deleting those on
	//      .veytan.com killed a concurrent PROD session in the same browser
	//      (prod still uses the unsuffixed names), and the re-writes created
	//      expiry-less duplicate cookies on .dev.veytan.com (header bloat).
	// Server-side sign-in is the correct owner of cookie cleanup.
	useEffect(() => {
		// Clear localStorage
		localStorage.clear();

		// Clear Redux state
		dispatch(setData({
			data: { userinfo: null },
			accesstoken: '',
			refreshtoken: ''
		}));
	}, []);

	// Preserve the `?redirect=` param when navigating to the next auth-FE page.
	// signin-new is the auth-FE entry point, so it also applies a legacy URL
	// fixup (dev-app.veytan.com → hcm.dev.veytan.com) for old bookmarks.
	// Mid-chain pages just propagate; only the terminal `buildFinalRedirect`
	// in workspace-selection falls back to DEFAULT_REDIRECT_URL.
	const preserveRedirect = (targetPath: string) => {
		const params = new URLSearchParams(location.search);
		const raw = params.get('redirect');
		if (raw && raw.includes('dev-app.veytan.com')) {
			// Apply legacy URL fixup before forwarding.
			const fixed = raw.replace(/dev-app\.veytan\.com/g, 'hcm.dev.veytan.com');
			return `${targetPath}?redirect=${encodeURIComponent(fixed)}`;
		}
		return preserveRedirectInPath(targetPath, location);
	};

	const checkNewUser = async () => {
		setLoading(true);
		await form.validateFields();
		const response = await checkUserStatus(form.getFieldsValue());
		
		if (response) {
			setLoading(false);
			if (response.error) {
				form.setFields([
					{
						name: 'email',
						errors: [response.message],
					},
				]);
			} else {
				message.success(response.message)
				setIsNewUser(response.data.is_first_time_login);
				setStep(2);
			}
		}
	};

	const userLogin = async (values: any) => {
		setLoading(true);
		await form.validateFields();
		
		const response = await handleUserLogin(values, dispatch, setData, navigate, location);
		
		if (response) {
			setLoading(false);
			if (response.error) {
				form.setFields([
					{
						name: 'password',
						errors: [response.message],
					},
				]);
			} else if (response.corporation_details?.length > 0) {
				// navigation already handled by handleUserLogin (corp selection screen)
			} else if (response.userinfo) {
				// Login successful - handle navigation
				if (response.userinfo.staffing_corp_access_list && 
					(response.userinfo.staffing_corp_access_list.corporation_details?.length > 0 || 
					response.userinfo.staffing_corp_access_list.staffing_details?.length > 0)) {
					// Redirect to workspace selection, preserving any redirect parameter
					navigate(preserveRedirect('/workspace-selection'));
				} else {
					// No workspace selection needed
					const queryParams = new URLSearchParams(location.search);
					let redirectUrl = queryParams.get('redirect') || PARAMS.DEFAULT_REDIRECT_URL;
					
					// Replace dev-app.veytan.com with hcm.dev.veytan.com
					redirectUrl = redirectUrl.replace(/dev-app\.veytan\.com/g, 'hcm.dev.veytan.com');

					// Extract the base URL (remove any paths and query parameters)
					const urlObj = new URL(redirectUrl);
					const baseUrl = `${urlObj.protocol}//${urlObj.host}`;

					const finalUrl = `${baseUrl}/home/dashboard`;

					// Build the terminal redirect with the SHARED, security-gated helper —
					// the same one workspace-selection uses. It appends `?token=` ONLY when
					// the target is localhost, and never for a deployed target.
					//
					// Why localhost needs the token: the BE's HttpOnly cookie lives on
					// .veytan.com, which is CROSS-SITE from http://localhost. Under
					// SameSite=Lax the browser will not send it to the API, so a local FE
					// cannot authenticate with it at all. It bootstraps instead from this
					// URL token, which vt-fe-hrms-01 stores as `veytan_localhost_jwt` and
					// its callAPI interceptor attaches as an Authorization: Bearer header
					// (a header is SameSite-independent).
					//
					// This branch previously hand-built the URL and omitted the token, so
					// when SameSite moved None -> Lax local dev lost BOTH paths — the
					// cross-site cookie AND the bridge. workspace-selection was unaffected
					// because it already used this helper.
					redirectUrl = buildFinalRedirect(finalUrl, {
						token: response?.accesstoken,
						workspaceData: response?.userinfo?.corporation_id
							? { corporation_id: response.userinfo.corporation_id }
							: undefined,
					});

					window.location.href = redirectUrl;
				}
			}
		}
	};

	const verifyCodeHandler = async (value: any) => {
		await form.validateFields();
		const email = form.getFieldValue('email');
		
		const response = await handleVerifyCode(value, email, isForgotPassword, navigate);
		console.log("response--", response);
		if (response.error && response.message) {
			message.error(response.message);
			return response;
		}
	};

	return (
		<LoginContainer>
			<EmailLogin
				form={form}
				checkNewUser={checkNewUser}
				userLogin={userLogin}
				isNewUser={isNewUser}
				step={step}
				setStep={setStep}
				loading={loading}
				verifyCode={verifyCodeHandler}
				setIsNewUser={setIsNewUser}
				setIsForgotPassword={setIsForgotPassword}
			/>
		</LoginContainer>
	);
};

export default Login;
